import {
  DateData,
  DurationData,
  LocationData,
  PersonsData,
  RoomsState,
  SearchBarWrapperSectionData,
} from '@/components/organisms/search-bar/search-bar.types';
import { AdaptCampaignsAvailableDates } from '@/integrations/travelportal/campaigns/available-dates/available-dates.adapter';
import { AdaptSearchAvailableDates } from '@/integrations/travelportal/offers/search/available-dates/available-dates.adapter';
import { getNumberOfDays } from '@/libraries/getters/get-number-of-days';

type GetHotelIdsParams = {
  hotelDetails?: Content.PageType.HotelPage['hotelDetails'];
  isWidget?: boolean;
  locationSection: SearchBarWrapperSectionData;
};

export type TravelPortalAreaDefaults = {
  defaultDepartureCode?: Content.Components['TravelPortalAvailableSearchArea']['defaultDepartureCode'];
  defaultPaxDistribution?: Content.Components['TravelPortalAvailableSearchArea']['selectedPaxDistribution'];
  defaultPeriodType?: Content.Components['TravelPortalAvailableSearchArea']['defaultPeriodType'];
  maxDepartureDate?: Content.Components['TravelPortalAvailableSearchArea']['maxDepartureDate'];
  minDepartureDate?: Content.Components['TravelPortalAvailableSearchArea']['minDepartureDate'];
  rangeSelect: Content.Components['TravelPortalAvailableSearchArea']['rangeSelect'];
};

/**
 * Merges two objects while preserving values from the first object
 * when the corresponding value in the second object is undefined.
 */
export const mergeObjects = (object1: any, object2: any) => {
  const mergedObj = { ...object1 };

  for (const key in object2) {
    if (object2[key] !== undefined) {
      mergedObj[key] = object2[key];
    }
  }

  return mergedObj;
};

/**
 * Resolves a TravelPortal area value from an area id.
 *
 * The helper searches `availableAreas` for an entry with a matching `id` and
 * treats the result as a `TravelPortalAvailableSearchArea`.
 *
 * @param travelPortalSearchSettings - TravelPortal search settings containing `availableAreas`.
 * @param id - Area identifier stored in search context or URL state.
 * @returns The resolved area `value` (for example `sun` or `snow`) or `undefined` when no matching search area exists.
 */
export const getArea = ({
  areaId,
  travelPortalSearchSettings,
}: {
  areaId: string;
  travelPortalSearchSettings?: Content.Components['TravelPortalSearchSettingsDto'];
}): TravelPortal.Properties.Area | undefined => {
  const localArea = travelPortalSearchSettings?.availableAreas?.find(
    area => area.id === areaId,
  ) as Content.Components['TravelPortalAvailableSearchArea'];

  return localArea?.value ?? undefined;
};

/**
 * Resolves a TravelPortal area value from an area value.
 *
 * The helper searches `availableAreas` for an entry with a matching `value` and
 * treats the result as a `TravelPortalAvailableSearchArea`.
 *
 * @param travelPortalSearchSettings - TravelPortal search settings containing `availableAreas`.
 * @param value - Area value stored in search context or URL state.
 * @returns The resolved area `value` (for example `sun` or `snow`) or `undefined` when no matching search area exists.
 */
export const getAreaByValue = ({
  travelPortalSearchSettings,
  value,
}: {
  travelPortalSearchSettings?: Content.Components['TravelPortalSearchSettingsDto'];
  value: string;
}): string | undefined => {
  const localArea = travelPortalSearchSettings?.availableAreas?.find(
    area =>
      (area as Content.Components['TravelPortalAvailableSearchArea']).value ===
      value,
  );

  return localArea?.id ?? undefined;
};

/**
 * Returns the default search values configured for the current travel portal area.
 *
 * The helper only considers search areas from `availableAreas` and ignores link areas.
 * The `currentAreaId` argument is matched against the area `id`.
 */
export const getTravelPortalAreaDefaults = (
  travelPortalSearchSettings?: Content.Components['TravelPortalSearchSettingsDto'],
  currentAreaId?: string,
): TravelPortalAreaDefaults => {
  const matchingArea = travelPortalSearchSettings?.availableAreas.find(
    (area): area is Content.Components['TravelPortalAvailableSearchArea'] =>
      area.$type === 'TravelPortalAvailableSearchArea' &&
      area.id === currentAreaId,
  );

  return {
    defaultDepartureCode: matchingArea?.defaultDepartureCode,
    defaultPaxDistribution: matchingArea?.selectedPaxDistribution,
    defaultPeriodType: matchingArea?.defaultPeriodType,
    maxDepartureDate: matchingArea?.maxDepartureDate,
    minDepartureDate: matchingArea?.minDepartureDate,
    rangeSelect: matchingArea?.rangeSelect ?? false,
  };
};

// Duration Section ----------------------

export const extractDurationPrimary = (
  durationSection?: SearchBarWrapperSectionData,
): string | undefined => {
  const durationData = durationSection?.dropdownData as DurationData;
  const selectedOption = durationData?.durationOptions.find(
    option => option.isSelected,
  );

  if (selectedOption) {
    return selectedOption.id;
  }

  return 'all';
};

export const extractDurationSecondary = (
  dateSection?: SearchBarWrapperSectionData,
): string | undefined => {
  const selectedOption = (
    (dateSection?.dropdownData as DateData)?.durationOptions ?? []
  ).find(option => option.isSelected);

  if (selectedOption) {
    return selectedOption.id;
  }

  return 'all';
};

export const extractDurationTertiary = (
  dateSection?: SearchBarWrapperSectionData,
): string | undefined => {
  const dateData = dateSection?.dropdownData as DateData;

  if (dateData?.durationOptions?.length === 0) {
    return undefined;
  }

  const selectedOption = (dateData?.durationOptions ?? []).find(
    option => option.isSelected,
  );

  if (selectedOption) {
    return selectedOption.id;
  }

  // Fallback to the first option if none is selected
  return dateData?.durationOptions?.length
    ? dateData.durationOptions[0].id
    : undefined;
};

export const extractDurationRange = (
  dateSection?: SearchBarWrapperSectionData,
): number | undefined => {
  const dateData = dateSection?.dropdownData as DateData;

  if (dateData.selectedDate && dateData.selectedMaxDate)
    return getNumberOfDays(dateData.selectedDate, dateData.selectedMaxDate);

  return undefined;
};

// Returns a unique array of all period types present in the available dates data
export const getAvailablePeriodTypesFromDates = (
  data: AdaptSearchAvailableDates | AdaptCampaignsAvailableDates,
): TravelPortal.Components['TravelPortalPeriodType'][] => {
  if (!data?.result) return [];

  const allTypes = data.result.flatMap(item => item.availablePeriodTypes || []);

  return Array.from(new Set(allTypes));
};

// Location Section ----------------------

export const extractCheckedLocations = (
  locationSection: SearchBarWrapperSectionData,
): string => {
  const locationData = locationSection?.dropdownData as LocationData;
  const checkedIds = locationData?.locations
    ? locationData.locations
        .flatMap(group => group.options)
        .filter(
          option =>
            option.checked &&
            !option.disabled &&
            option.nodeType === 'destination',
        )
        .map(option => option.id)
    : [];

  const destinationIds = locationData.destinations ?? [];

  // Combine and remove duplicates
  const uniqueIds = Array.from(new Set([...checkedIds, ...destinationIds]));

  return uniqueIds.length > 0 ? uniqueIds.join(',') : '';
};

export interface TravelPortalLocationCodes {
  countryIds: string;
  destinationIds: string;
}

export const extractTravelPortalLocationCodes = (
  locationSection: SearchBarWrapperSectionData,
): TravelPortalLocationCodes => {
  const locationData = locationSection?.dropdownData as LocationData;

  if (!locationData?.locations || locationData.locations.length === 0) {
    return { countryIds: '', destinationIds: '' };
  }

  const countryCodes: string[] = [];
  const destinationIds: string[] = [];

  for (const group of locationData.locations) {
    const selectableDestinations = group.options.filter(
      option => option.nodeType === 'destination' && !option.disabled,
    );
    const checkedDestinations = selectableDestinations.filter(
      option => option.checked,
    );

    if (checkedDestinations.length === 0) {
      continue;
    }

    if (
      group.countryCode &&
      selectableDestinations.length > 0 &&
      checkedDestinations.length === selectableDestinations.length
    ) {
      countryCodes.push(group.countryCode);
      continue;
    }

    destinationIds.push(...checkedDestinations.map(option => option.id));
  }

  const uniqueCountryIds = Array.from(new Set(countryCodes));
  const uniqueDestinationIds = Array.from(new Set(destinationIds));

  return {
    countryIds: uniqueCountryIds.length > 0 ? uniqueCountryIds.join(',') : '',
    destinationIds:
      uniqueDestinationIds.length > 0 ? uniqueDestinationIds.join(',') : '',
  };
};

export const extractDestinationRegionIds = (
  locationSection: SearchBarWrapperSectionData,
): string => {
  const locationData = locationSection?.dropdownData as LocationData;
  const checkedIds = locationData.locations
    ? locationData.locations
        .flatMap(group => group.options)
        .filter(
          option =>
            option.checked &&
            !option.disabled &&
            option.nodeType === 'destinationRegion',
        )
        .map(option => option.id)
    : [];

  const destinationRegionIds = locationData.destinationRegionIds ?? [];

  // Combine and remove duplicates
  const uniqueIds = Array.from(
    new Set([...checkedIds, ...destinationRegionIds]),
  );

  return uniqueIds.length > 0 ? uniqueIds.join(',') : '';
};

export const extractHotelIds = ({
  locationSection,
  isWidget,
  hotelDetails,
}: GetHotelIdsParams): string => {
  if (isWidget) {
    const hotelId = hotelDetails?.hotelId;

    if (Array.isArray(hotelId)) {
      return hotelId.map(String).join(',');
    } else if (typeof hotelId === 'number') {
      return hotelId.toString();
    } else {
      return '';
    }
  }

  const locationData = locationSection?.dropdownData as LocationData;

  return locationData.hotelIds?.join(',') || '';
};

export const extractTransportationMethod = (
  locationSection?: SearchBarWrapperSectionData,
  isWidget?: boolean,
  departureIds?: string,
): string | undefined => {
  const locationData = locationSection?.dropdownData as LocationData;

  const selectedOption = locationData?.transportationOptions.find(
    option => option.isSelected,
  );

  if (selectedOption) {
    return selectedOption.id;
  }

  if (!isWidget && departureIds) {
    return departureIds;
  }

  return undefined;
};

// Persons Section ----------------------

export const extractAdultCount = (
  personsSection: SearchBarWrapperSectionData,
): number | undefined => {
  const personsData = personsSection?.dropdownData as PersonsData;

  return personsData.roomsState?.rooms.reduce((total, room) => {
    return total + room.numberOfAdults;
  }, 0);
};

export const extractChildrenAges = (
  personsSection: SearchBarWrapperSectionData,
): string => {
  const personsData = personsSection?.dropdownData as PersonsData;

  const allAges = personsData.roomsState?.rooms
    .flatMap(room => room.childrenAges)
    .filter((age): age is number => age !== null && age !== undefined);

  return allAges?.join(',') ?? '';
};

export const extractAdultsPerRoom = (
  personsSection: SearchBarWrapperSectionData,
): string => {
  const personsData = personsSection?.dropdownData as PersonsData;

  const adultsCounts = personsData.roomsState?.rooms.map(room =>
    room?.numberOfAdults?.toString(),
  );

  return (adultsCounts && adultsCounts.join('|')) ?? '';
};

export const extractChildrenAgesPerRoom = (
  personsSection: SearchBarWrapperSectionData,
): string => {
  const personsData = personsSection?.dropdownData as PersonsData;

  const childrenAgesPerRoom = personsData.roomsState?.rooms.map(room =>
    room.numberOfChildren > 0
      ? room.childrenAges.filter(age => age !== null).join(',')
      : '',
  );

  return (childrenAgesPerRoom && childrenAgesPerRoom.join('|')) ?? '';
};

export const extractAutomaticRoomAssignment = (
  personsSection: SearchBarWrapperSectionData,
): boolean => {
  const personsData = personsSection?.dropdownData as PersonsData;
  const automaticRoomAssignment =
    personsData.roomsState?.automaticRoomAssignment;

  return automaticRoomAssignment ?? true;
};

export const isPersonsSectionValid = (
  personSection: SearchBarWrapperSectionData,
): boolean => {
  const personsData = personSection.dropdownData as PersonsData;

  if (!personsData.roomsState) {
    return false;
  }

  return personsData.roomsState?.rooms.every(room => {
    return (
      room.numberOfChildren === room.childrenAges.length &&
      room.childrenAges.every(age => age !== null)
    );
  });
};

export const generatePersonsSectionValueString = ({
  adults,
  children,
  roomsState,
  t,
}: {
  adults?: number;
  children?: number;
  roomsState?: RoomsState;
  t: (key: string) => string;
}): string | undefined => {
  const totalAdults = roomsState
    ? roomsState.rooms.reduce((sum, room) => sum + room.numberOfAdults, 0)
    : (adults ?? 0);

  const totalChildren = roomsState
    ? roomsState.rooms.reduce((sum, room) => sum + room.numberOfChildren, 0)
    : (children ?? 0);

  if (totalAdults === 0) {
    return undefined;
  }

  const adultKey =
    totalAdults === 1
      ? 'items.common.words.adult'
      : 'items.common.words.adults';

  const childKey =
    totalChildren === 1
      ? 'items.common.words.child'
      : 'items.common.words.children';

  const adultsString = `${totalAdults} ${t(adultKey)}`;

  if (totalChildren > 0) {
    return `${adultsString}, ${totalChildren} ${t(childKey)}`;
  }

  return adultsString;
};

// Date Section ----------------------

export const extractDepartureDate = (
  dateSection?: SearchBarWrapperSectionData,
): string | undefined => {
  const date = (dateSection?.dropdownData as DateData)?.selectedDate;

  return date?.toDateString();
};

/**
 * Extracts the departure date as ISO 8601 string for cookie persistence.
 * ISO format ensures reliable round-tripping across browsers and timezones.
 */
export const extractDepartureDateAsISO = (
  dateSection?: SearchBarWrapperSectionData,
): string | undefined => {
  const date = (dateSection?.dropdownData as DateData)?.selectedDate;

  return date?.toISOString();
};

export const extractMaxDepartureDate = (
  dateSection?: SearchBarWrapperSectionData,
): string | undefined => {
  const date = (dateSection?.dropdownData as DateData)?.selectedMaxDate;

  return date?.toDateString();
};

export const generateDateSectionValueString = ({
  duration,
  endDate,
  startDate,
  t,
}: {
  duration: string | undefined;
  endDate?: string | undefined;
  startDate: string | undefined;
  t: (key: string) => string;
}): string => {
  let prefix = '';

  switch (duration) {
    case 'miniWeek':
      prefix = `${t('items.common.periodType.miniWeek')}; `;
      break;

    case 'week':
      prefix = `${t('items.common.periodType.week')}; `;
      break;

    case 'twoWeeks':
      prefix = `${t('items.common.periodType.twoWeeks')}; `;
      break;

    case 'threeWeeks':
      prefix = `${t('items.common.periodType.threeWeeks')}; `;
      break;

    case 'custom':
      if (startDate && endDate) {
        return `${startDate} ${t('items.search.searchBar.date.to')} ${endDate}`;
      } else {
        return '';
      }

    default:
      prefix = '';
  }

  if (startDate) {
    return `${prefix}${t('items.common.words.from')} ${startDate}`;
  }

  return `${prefix}`;
};

/**
 * Filters campaign available dates by period type and optional departure code.
 *
 * The function keeps dates that include the provided `durationId` in
 * `availablePeriodTypes`. When `selectedDepartureCode` is provided, it also
 * keeps only dates that contain that departure in `availableDepartures`.
 *
 * @param availableDatesData - Campaign available dates response data.
 * @param durationId - Selected period type used to filter available dates.
 * @param selectedDepartureCode - Optional departure code to additionally filter by.
 * @returns A list of Date objects created from matching `departureDate` values.
 */
export const filterCampaignAvailableDates = ({
  availableDatesData,
  durationId,
  selectedDepartureCode,
}: {
  availableDatesData: AdaptCampaignsAvailableDates;
  durationId: TravelPortal.Properties.PeriodType;
  selectedDepartureCode?: string;
}): Date[] => {
  if (!availableDatesData?.result) return [];

  return availableDatesData.result
    .filter(date => {
      const matchesDeparture = selectedDepartureCode
        ? date.availableDepartures.some(
            departure => departure.departureCode === selectedDepartureCode,
          )
        : true;

      const matchesPeriodType = date.availablePeriodTypes.includes(
        durationId as TravelPortal.Properties.PeriodType,
      );

      return matchesDeparture && matchesPeriodType;
    })
    .map(date => new Date(date.departureDate));
};
